import { IconName } from '@infotech-soe/erp-ui-kit';
import { TDatasetQueryParams } from '@infotech-soe/merps/types/Queries';
import {
  DatasetStyleRuleCondition,
  IDatasetCommands,
  IDatasetInputColumns,
  IDatasetInputControl,
  IDisplayModeEditableFieldMD,
  TContextMenuItemBadge,
} from '@infotech-soe/metaschema';
import defaultsDeep from 'lodash/defaultsDeep';

import { FEATURES, formGridFeatures } from '../../../../constants/dataset';
import CachedSchemaRepository from '../../../../infrastructure/schemas/storage/CachedSchemaRepository';
import { NamedDomain } from '../../../../infrastructure/schemas/storage/NamedDomain';
import { CaptionType } from '../../../../models/caption/Caption';
import {
  DatasetConfig,
  DatasetPresetRule,
} from '../../../../models/user-config/DisplayModeConfig';
import {
  CommandListEntryType,
  CommandListEntryViewModel,
} from '../../../commands/CommandViewModel';
import {
  DatasetColumnFormat,
  DatasetGridColumn,
} from '../../../dataset/view-model/DatasetGridColumn';
import IntlService from '../../../intl/IntlService';
import {
  DatasetFormControlViewModel,
  FormControlViewModelType,
  LocalizedProps,
} from '../../view-model/FormControlViewModel';
import { FormViewModel } from '../../view-model/FormViewModel';

export const getEditableFields = (
  projection: IDatasetInputColumns
): Record<string, IDisplayModeEditableFieldMD> | undefined => {
  const result: Record<string, IDisplayModeEditableFieldMD> = {};
  for (const key in projection) {
    if (Object.hasOwn(projection, key) && projection[key].editable) {
      result[key] = {
        action: '',
        submitDirection: projection[key].submitDirection ?? 'down',
        required: projection[key].required,
        dictionary: projection[key].dictionary,
      };
    }
  }

  return Object.keys(result).length ? result : undefined;
};

export const getRules = (
  projection: IDatasetInputColumns,
  rules: IDatasetInputControl['rules'] | undefined
): Record<string, DatasetPresetRule> => {
  const result: Record<string, DatasetPresetRule> = {};
  for (const col in projection) {
    if (Object.hasOwn(projection, col)) {
      const defaultCondition = {
        value: {
          before: null,
          after: null,
          isApplyRuleToRow: false,
          textBold: false,
          textItalic: false,
          textUnderline: false,
          textLineThrough: false,
        },
        color: {
          color: '',
          backgroundColor: '',
        },
      };
      const rulesConditions: DatasetStyleRuleCondition[] = [];
      rules?.[col]?.conditions?.forEach(condition => {
        rulesConditions.push(defaultsDeep({}, condition, defaultCondition));
      });
      result[col] = {
        visibility: true,
        conditions: rulesConditions,
      };
    }
  }
  return result;
};

const buildDatasetInputControl = async (
  item: IDatasetInputControl,
  localizedProps: LocalizedProps,
  viewModel: FormViewModel
): Promise<DatasetFormControlViewModel> => {
  let labelTitle = '';
  const intlService = new IntlService();
  for (const key in item.projection) {
    if (Object.hasOwn(item.projection, key)) {
      if (item.projection[key].isTitle) {
        labelTitle = key;
      }
    }
  }

  const getFormat = (format: NamedDomain): DatasetColumnFormat => {
    switch (format.type) {
      case 'number':
        return DatasetColumnFormat.NUMBER;
      case 'enum':
        return DatasetColumnFormat.ENUM;
      case 'object':
        if (format.class === 'Date') {
          return DatasetColumnFormat.DATE;
        } else {
          return DatasetColumnFormat.STRING;
        }
      default:
        return DatasetColumnFormat.STRING;
    }
  };

  const getColumns = async (): Promise<DatasetGridColumn[]> => {
    const result: DatasetGridColumn[] = [];
    const schemaRepository = new CachedSchemaRepository();
    for (const col in item.projection) {
      if (Object.hasOwn(item.projection, col)) {
        const domain = await schemaRepository.getDomain(
          item.projection[col].domain
        );

        const localizedTitle =
          (await intlService.getCategoryFieldTitle(
            viewModel.source.category,
            item.projection[col].resId
          )) ||
          (await intlService.getApplicationMenuTitle(
            item.projection[col].resId
          ));

        const localizations = new Map();
        for (const value of domain?.values ?? []) {
          localizations.set(
            value,
            await intlService.getDomainEnumValue(domain.name, value)
          );
        }

        const formatter = (value: any) => localizations.get(value);

        result.push({
          domain,
          field: col,
          title: localizedTitle,
          format: getFormat(domain),
          ...(domain.type === 'enum' ? { formatter } : {}),
          notResizable: item.projection[col].width !== undefined,
        });
      }
    }
    return result;
  };

  const conditions: TDatasetQueryParams = {
    fieldsConditions: {},
    conditions: {},
    queryOptions: { caseSensitive: false },
  };

  const getColumnsOrder = () => {
    const savedFormConfigDatasetInputColumnsOrder =
      'formConfig' in viewModel.source
        ? ((viewModel.source.formConfig[item.name]?.columnsOrder as string[]) ??
          [])
        : [];
    const projectionColumnsOrder = Object.keys(item.projection);
    const availableSavedFormConfigDatasetInputColumnsOrder =
      savedFormConfigDatasetInputColumnsOrder.filter(columnName =>
        projectionColumnsOrder.includes(columnName)
      );
    const availableDatasetInputProjectionColumnsOrder =
      projectionColumnsOrder.filter(
        columnName =>
          !savedFormConfigDatasetInputColumnsOrder.includes(columnName)
      );

    return [
      ...availableSavedFormConfigDatasetInputColumnsOrder,
      ...availableDatasetInputProjectionColumnsOrder,
    ];
  };

  const getColumnsWidth = () => {
    const saved = (
      'formConfig' in viewModel.source
        ? (viewModel.source.formConfig[item.name]?.columnsWidth ?? {})
        : {}
    ) as DatasetConfig['columnsWidth'];
    return Object.entries(item.projection).reduce(
      (acc, [k, v]) => {
        acc[k] = v.width ?? saved[k];
        return acc;
      },
      <DatasetConfig['columnsWidth']>{}
    );
  };

  const config: DatasetConfig = {
    columnsOrder: getColumnsOrder().filter((columnName: string) => {
      if (formGridFeatures.includes(FEATURES.LABEL) && labelTitle) {
        return columnName !== labelTitle;
      }
      return columnName;
    }),
    preset: {
      default: {
        pinned: item.pinned ?? 0,
        rules: getRules(item.projection, item.rules),
      },
    },
    currentPreset: 'default',
    columnsWidth: getColumnsWidth(),
    sort: [],
    pinned: 0,
  };

  const getCommands = async (
    commands?: IDatasetCommands
  ): Promise<CommandListEntryViewModel[]> => {
    const result: CommandListEntryViewModel[] = [];
    const getBadge = (
      badge?: TContextMenuItemBadge
    ): string | number | undefined => {
      if (typeof badge === 'boolean') {
        return badge ? '✓' : undefined;
      }
      return badge;
    };
    for (const key in commands) {
      if (Object.hasOwn(commands, key)) {
        result.push({
          type: CommandListEntryType.COMMAND,
          name: key,
          icon: (commands[key].icon ?? '') as IconName,
          caption: {
            type: CaptionType.EXTERNAL,
            caption: await intlService.getCategoryMessage(
              viewModel.source.category,
              commands[key].resId ?? key
            ),
          },
          available: true,
          badge: getBadge(commands[key].badge),
        });
      }
    }
    return result;
  };

  return {
    conditions,
    config,
    labelTitle,
    editableFields: getEditableFields(item.projection),
    resId: item.resId,
    disabled: item.disabled,
    required: item.required,
    readonly: item.readonly,
    visible: item.visible,
    projection: item.projection,
    commands: await getCommands(item.commands),
    totals: item.totals || 0,
    columns: item.columns,
    columnsData: await getColumns(),
    control: item.control,
    rows: item.rows || 3,
    value: null,
    hasValue: true,
    hasHistory: false,
    name: item.name,
    noLabel: item.noLabel,
    labelWidth: item.labelWidth,
    storeValue: item.storeValue,
    type: FormControlViewModelType.DATASET,
    ...localizedProps,
    rules: item.rules,
    disableMoveColumn: item.disableMoveColumn,
    fullHeight: item.fullHeight,
  };
};

export default buildDatasetInputControl;
